/**
 * Cross-Device Sync Service
 *
 * Syncs local IndexedDB data to Supabase for cross-device access.
 * Works automatically when user is authenticated.
 */

import { useEffect, useCallback, useState } from 'react';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { db } from '@/db/database';
import type { User } from '@supabase/supabase-js';

// Generate or retrieve a stable device ID
function getDeviceId(): string {
  const key = 'mapquizzr_device_id';
  let deviceId = localStorage.getItem(key);

  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(key, deviceId);
  }

  return deviceId;
}

export interface SyncStatus {
  isSyncing: boolean;
  lastSyncAt: Date | null;
  error: string | null;
}

export function useSyncService(user: User | null) {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    isSyncing: false,
    lastSyncAt: null,
    error: null,
  });

  const deviceId = getDeviceId();

  /**
   * Pull remote data from Supabase and merge into local IndexedDB
   */
  const pullFromRemote = useCallback(async () => {
    if (!user || !isSupabaseConfigured) return;

    console.log('📥 Pulling data from Supabase...');

    try {
      // Fetch all sync records for this user
      const { data: syncRecordsRaw, error } = await supabase
        .from('sync_records')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Type assertion for sync records
      const syncRecords = syncRecordsRaw as Array<{
        id: string;
        record_type: 'quiz_session' | 'knowledge_state' | 'quiz_config';
        record_id: string;
        data: any;
        created_at: string;
      }> | null;

      if (!syncRecords || syncRecords.length === 0) {
        console.log('No remote data to sync');
        return;
      }

      // Group by record type and ID, keeping only the latest version
      const latestRecords = new Map<string, any>();

      for (const record of syncRecords) {
        const key = `${record.record_type}:${record.record_id}`;
        const existing = latestRecords.get(key);

        if (!existing || new Date(record.created_at) > new Date(existing.created_at)) {
          latestRecords.set(key, record);
        }
      }

      // Apply each record to local database
      let quizConfigCount = 0;
      let sessionCount = 0;
      let knowledgeCount = 0;

      for (const [, record] of latestRecords) {
        const remoteData = record.data as any;

        try {
          switch (record.record_type) {
            case 'quiz_config':
              // For quiz configs: keep the one with most recent lastUsedAt
              const existingConfig = await db.quiz_configs.get(record.record_id);
              if (!existingConfig || new Date(remoteData.lastUsedAt) > new Date(existingConfig.lastUsedAt)) {
                await db.quiz_configs.put(remoteData);
                quizConfigCount++;
              }
              break;

            case 'quiz_session':
              // Sessions have unique IDs - never conflict, just add if missing
              const existingSession = await db.quiz_sessions.get(record.record_id);
              if (!existingSession) {
                await db.quiz_sessions.put(remoteData);
                sessionCount++;
              }
              // If it already exists, it's a duplicate - skip it
              break;

            case 'knowledge_state':
              // Knowledge states: use the most recently updated version
              const existingKnowledge = await db.knowledge_state.get(record.record_id);

              if (!existingKnowledge) {
                // New knowledge state, just add it
                await db.knowledge_state.put(remoteData);
                knowledgeCount++;
              } else {
                // Keep the version with the most recent update
                if (new Date(remoteData.lastUpdated) > new Date(existingKnowledge.lastUpdated)) {
                  await db.knowledge_state.put(remoteData);
                  knowledgeCount++;
                }
                // Otherwise keep existing (more recent)
              }
              break;
          }
        } catch (err) {
          console.warn(`Failed to apply sync record ${record.id}:`, err);
        }
      }

      console.log(`✅ Synced from remote: ${quizConfigCount} configs, ${sessionCount} sessions, ${knowledgeCount} knowledge states`);
    } catch (error) {
      console.error('Failed to pull from remote:', error);
      throw error;
    }
  }, [user]);

  /**
   * Push local data to Supabase
   */
  const pushToRemote = useCallback(async () => {
    if (!user || !isSupabaseConfigured) return;

    console.log('📤 Pushing data to Supabase...');

    try {
      const syncRecords: any[] = [];

      // Push quiz configs
      const quizConfigs = await db.quiz_configs.toArray();
      for (const config of quizConfigs) {
        syncRecords.push({
          user_id: user.id,
          device_id: deviceId,
          record_type: 'quiz_config' as const,
          record_id: config.signature,
          data: config,
        });
      }

      // Push quiz sessions
      const sessions = await db.quiz_sessions.toArray();
      for (const session of sessions) {
        syncRecords.push({
          user_id: user.id,
          device_id: deviceId,
          record_type: 'quiz_session' as const,
          record_id: session.id,
          data: session,
        });
      }

      // Push knowledge state
      const knowledgeStates = await db.knowledge_state.toArray();
      for (const state of knowledgeStates) {
        syncRecords.push({
          user_id: user.id,
          device_id: deviceId,
          record_type: 'knowledge_state' as const,
          record_id: state.id,
          data: state,
        });
      }

      if (syncRecords.length === 0) {
        console.log('No local data to push');
        return;
      }

      // Upsert sync records (ON CONFLICT, update if exists)
      const { error } = await supabase
        .from('sync_records')
        .upsert(syncRecords as any, {
          onConflict: 'user_id,device_id,record_type,record_id',
        });

      if (error) throw error;

      console.log(`✅ Pushed ${syncRecords.length} records to remote`);
    } catch (error) {
      console.error('Failed to push to remote:', error);
      throw error;
    }
  }, [user, deviceId]);

  /**
   * Full sync: Pull then Push
   */
  const sync = useCallback(async () => {
    if (!user || !isSupabaseConfigured) {
      console.log('⏭️ Sync skipped: not authenticated or Supabase not configured');
      return;
    }

    setSyncStatus((prev) => ({ ...prev, isSyncing: true, error: null }));

    try {
      console.log('🔄 Starting full sync...');

      // Pull first to get latest remote data
      await pullFromRemote();

      // Then push local data
      await pushToRemote();

      setSyncStatus({
        isSyncing: false,
        lastSyncAt: new Date(),
        error: null,
      });

      console.log('✅ Sync complete!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ Sync failed:', errorMessage);

      setSyncStatus({
        isSyncing: false,
        lastSyncAt: null,
        error: errorMessage,
      });
    }
  }, [user, pullFromRemote, pushToRemote]);

  /**
   * Auto-sync on mount when user is authenticated
   */
  useEffect(() => {
    if (user && isSupabaseConfigured) {
      console.log('👤 User authenticated, triggering initial sync...');
      sync();
    }
  }, [user?.id]); // Only trigger when user ID changes

  /**
   * Periodic sync every 5 minutes while authenticated
   */
  useEffect(() => {
    if (!user || !isSupabaseConfigured) return;

    const interval = setInterval(() => {
      console.log('⏰ Periodic sync triggered');
      sync();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [user?.id, sync]);

  return {
    sync,
    syncStatus,
    pullFromRemote,
    pushToRemote,
  };
}
